import { AbsenceOrigin } from '@bp/bp-graphql-types';
import { ProfileRoles } from '@bp/pim-auth-constants';
import { Button, LazyLoader } from '@bp/ui-components';
import { graphql } from 'client/gql';
import { BpCard } from 'components/BpCard/BpCard';
import dayjs, { Dayjs } from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'urql';
import { mapCalendarEventsToBpEventType } from '../../../../hooks/useCalendarEvents';
import { showSuccessUpdateToast, showUserDbErrorToast } from '../../../../utils/toasts';
import { BpTipTap } from '../../../BpTipTap/BpTipTap';
import { ClassbookAttendeeList } from '../AttendeeList/ClassbookAttendeeList';
import type { AbsenceType, Attendee } from '../AttendeeList/ClassbookAttendeeListItem';
import styles from './ClassbookEvent.module.scss';

const CreateAbsenceMutationDocument = graphql(`
  mutation BpCreateAbsence($input: [AbsenceCreateInput!]!) {
    createAbsences(input: $input) {
      absences {
        uuid
        __typename
      }
    }
  }
`);
const DeleteAbsenceMutationDocument = graphql(`
  mutation BpDeleteAbsenceFromClassbookEvent($where: AbsenceWhere!) {
    deleteAbsences(where: $where) {
      nodesDeleted
    }
  }
`);
const UpdateAbsenceMutationDocument = graphql(`
  mutation BpUpdateAbsenceInClassbookEvent($where: AbsenceWhere!, $update: AbsenceUpdateInput!) {
    updateAbsences(where: $where, update: $update) {
      absences {
        uuid
        __typename
        excused
        startTime
        endTime
      }
    }
  }
`);
const UpdateCalendarEventsMutationDocument = graphql(`
  mutation BpUpdateCalendarEventsInClassbookEvent($where: CalendarEventWhere!, $update: CalendarEventUpdateInput!) {
    updateCalendarEvents(where: $where, update: $update) {
      calendarEvents {
        uuid
        __typename
        classbookNote
        classbookHomework
        attendees {
          uuid
          __typename
        }
      }
      info {
        nodesCreated
      }
    }
  }
`);

export type ClassbookEventProps = {
  eventUuid: string | null;
  isWholeDay: ({ start, end }: { start: Dayjs; end: Dayjs }) => boolean;
};

const ClassbookEventQueryDocument = graphql(`
  query ClassbookEventQueryDocument(
    $calendarEventWhere: CalendarEventWhere!
    $absenceReasonsWhere: AbsenceReasonWhere!
  ) {
    ...CalendarEventMapCalendarEventsToBpEventTypeFields
    ...ClassbookAttendeeListFields
  }
`);

const ClassbookAbsenceQueryDocument = graphql(`
  query ClassbookAbsenceData($eventAttendeeUuids: [ID!]!, $start: DateTime!, $end: DateTime!) {
    profiles(where: { uuid_IN: $eventAttendeeUuids }) {
      uuid
      __typename
      organizationRoles
      firstName
      lastName
    }
    absences(
      where: {
        owner: { uuid_IN: $eventAttendeeUuids }
        OR: [
          { startTime_LTE: $start, endTime_GTE: $start }
          { startTime_GTE: $end, endTime_LTE: $end }
          { startTime_GTE: $start, endTime_LTE: $end }
        ]
      }
    ) {
      uuid
      __typename
      owner {
        uuid
        __typename
      }
      startTime
      endTime
      reason {
        uuid
        __typename
      }
      excused
    }
  }
`);

export const ClassbookEvent = ({ eventUuid, isWholeDay }: ClassbookEventProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [noteCollapsed, setNoteCollapsed] = useState<boolean>(true);
  const [note, setNote] = useState<string | undefined>();
  const [homeworkCollapsed, setHomeworkCollapsed] = useState<boolean>(true);
  const [homework, setHomework] = useState<string | undefined>();
  const [updating, setUpdating] = useState<'note' | 'homework' | null>(null);

  const [, updateCalendarEvent] = useMutation(UpdateCalendarEventsMutationDocument);
  const [, createAbsence] = useMutation(CreateAbsenceMutationDocument);
  const [, updateAbsence] = useMutation(UpdateAbsenceMutationDocument);
  const [, deleteAbsence] = useMutation(DeleteAbsenceMutationDocument);

  const [{ data: eventData }] = useQuery({
    query: ClassbookEventQueryDocument,
    variables: {
      calendarEventWhere: {
        uuid: eventUuid,
      },
      absenceReasonsWhere: {
        organization: { uuid: organizationUuid },
        AND: [{ internal: false }],
      },
    },
  });

  const handleNoteSave = async () => {
    setUpdating('note');

    const { error } = await updateCalendarEvent({
      where: { uuid: eventUuid },
      update: { classbookNote: note },
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }
    setUpdating(null);
  };

  const handleHomeworkSave = async () => {
    setUpdating('homework');
    const { error } = await updateCalendarEvent({
      where: { uuid: eventUuid },
      update: { classbookHomework: homework },
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }
    setUpdating(null);
  };

  const event = useMemo(() => {
    // since we are not interested in "owner"-field as boolean indicator, we can pass empty string as currentProfileUuid and false as isAdmin
    return mapCalendarEventsToBpEventType(eventData, '', false)?.find((e) => e.uuid === eventUuid);
  }, [eventUuid, eventData]);

  const duration = useMemo(() => {
    return dayjs.duration(Number(event?.duration), 's');
  }, [event]);

  const start = useMemo(() => {
    return dayjs(event?.start);
  }, [event]);

  const end = useMemo(() => {
    return dayjs(start).add(Number(duration));
  }, [start, duration]);

  const [{ data: absencesData }] = useQuery({
    query: ClassbookAbsenceQueryDocument,
    variables: {
      eventAttendeeUuids: event?.attendees.map((a) => a.uuid) ?? [],
      start,
      end,
    },
    pause: eventUuid === null,
  });

  const attendees: Attendee[] = useMemo(() => {
    const profiles = absencesData?.profiles ?? [];
    const absences = absencesData?.absences ?? [];

    return (
      profiles
        .filter((p) => p.organizationRoles.includes(ProfileRoles.Student))
        .sort((a, b) => {
          if (!a.lastName || !b.lastName) return 0;
          if (!a.firstName || !b.firstName) return 0;

          // First, compare by lastName
          if (a.lastName < b.lastName) return -1;
          if (a.lastName > b.lastName) return 1;

          // If lastNames are equal, then compare by firstName
          if (a.firstName < b.firstName) return -1;
          if (a.firstName > b.firstName) return 1;

          // If both lastName and firstName are equal, return 0
          return 0;
        })
        .map((profile) => {
          const absenseForProfile =
            absences.find((absence) => {
              return absence.owner.uuid === profile.uuid;
            }) ?? null;

          return {
            uuid: profile.uuid,
            name: profile.firstName + ' ' + profile.lastName,
            comment: event?.attendees.find((a) => a.uuid === profile.uuid)?.classbookComment,
            lateInMinutes: event?.attendees.find((a) => a.uuid === profile.uuid)?.lateDurationMinutes,
            absence: absenseForProfile
              ? {
                  attendeeUuid: profile.uuid,
                  uuid: absenseForProfile.uuid,
                  absentFrom: dayjs(absenseForProfile.startTime) ?? dayjs(),
                  absentUntil: dayjs(absenseForProfile.endTime) ?? dayjs(),
                  absenceReason: absenseForProfile.reason?.uuid ?? null,
                  excused: absenseForProfile.excused ?? false,
                  status:
                    absenseForProfile.excused === true
                      ? 'excused'
                      : isWholeDay({
                            start: dayjs(absenseForProfile.startTime),
                            end: dayjs(absenseForProfile.endTime),
                          })
                        ? 'wholeDay'
                        : 'absent',
                }
              : null,
          };
        }) ?? []
    );
  }, [absencesData, event, isWholeDay]);

  async function onAbsenceUpdate(updatedAbsence: Partial<AbsenceType> & { comment?: string }) {
    let connectAbsence = {};
    if (updatedAbsence.absenceReason) {
      connectAbsence = {
        connect: { where: { node: { uuid: updatedAbsence.absenceReason } } },
      };
    }

    if (updatedAbsence.uuid && updatedAbsence.status === 'present') {
      await deleteAbsence({ where: { uuid: updatedAbsence.uuid } });
    } else if (
      updatedAbsence.uuid === undefined &&
      updatedAbsence.status !== undefined &&
      updatedAbsence.status !== 'present'
    ) {
      await createAbsence({
        input: {
          originUuid: profileUuid,
          origin: AbsenceOrigin.Classbook,
          foreignRefId: [],
          startTime: start.toDate(),
          endTime: end.toDate(),
          excused: updatedAbsence.excused ?? false,
          reason: { ...connectAbsence },
          owner: {
            connect: {
              where: {
                node: {
                  uuid: updatedAbsence.attendeeUuid,
                },
              },
            },
          },
        },
      });
    } else if (updatedAbsence.uuid !== undefined) {
      await updateAbsence({
        where: {
          uuid: updatedAbsence.uuid,
        },
        update: {
          startTime: updatedAbsence.absentFrom?.toDate(),
          endTime: updatedAbsence.absentUntil?.toDate(),
          excused: updatedAbsence.excused ?? false,
          reason: { disconnect: { where: {} }, ...connectAbsence },
        },
      });
    }

    if (
      (updatedAbsence.comment !== undefined || updatedAbsence.lateInMinutes !== undefined) &&
      event?.uuid &&
      updatedAbsence.attendeeUuid
    ) {
      if (updatedAbsence.comment === '{"type":"doc","content":[{"type":"paragraph"}]}') {
        updatedAbsence.comment = '';
      }
      await updateCalendarEvent({
        where: { uuid: event?.uuid },
        update: {
          attendees: [
            {
              connect: [
                {
                  overwrite: true,
                  where: { node: { uuid: updatedAbsence.attendeeUuid } },
                  edge: { classbookComment: updatedAbsence.comment, lateDurationMinutes: updatedAbsence.lateInMinutes },
                },
              ],
            },
          ],
        },
      });
    }
  }

  return (
    <div className={styles['classbook-event']}>
      <BpCard
        className='mb-7'
        alwaysCollapsible
        disableCollapsible={!event}
        isCollapsed={noteCollapsed}
        onCollapse={() => setNoteCollapsed(!noteCollapsed)}
        header={{ headline: t('notes.title', { count: 2 }) }}
      >
        <BpTipTap key={event?.uuid} defaultValue={event?.classbookNote ?? ''} onChange={setNote} name='note' isDense />
        <div className={styles.actions}>
          <Button onClick={handleNoteSave} isLoading={updating === 'note'}>
            {t('common.save')}
          </Button>
        </div>
      </BpCard>

      <BpCard
        className='mb-7'
        alwaysCollapsible
        disableCollapsible={!event}
        isCollapsed={homeworkCollapsed}
        onCollapse={() => setHomeworkCollapsed(!homeworkCollapsed)}
        header={{ headline: t('homeworks.title', { count: 2 }) }}
      >
        <BpTipTap
          key={event?.uuid}
          defaultValue={event?.classbookHomework ?? ''}
          onChange={setHomework}
          name='homework'
          isDense
        />
        <div className={styles.actions}>
          <Button onClick={handleHomeworkSave} isLoading={updating == 'homework'}>
            {t('common.save')}
          </Button>
        </div>
      </BpCard>

      <BpCard
        noPadding
        alwaysCollapsible
        disableCollapsible={!event}
        isCollapsed={!event}
        header={{
          headline: t('students.title', { count: 2 }),
          subHeadline: event
            ? t('classbook.attendantHint', {
                actual: attendees.filter((attendee) => attendee.absence === null).length,
                desired: attendees.length,
              })
            : undefined,
        }}
      >
        <ClassbookAttendeeList
          key={event?.uuid}
          attendees={attendees}
          event={event ?? null}
          onAbsenceUpdate={onAbsenceUpdate}
          query={eventData}
        />
      </BpCard>
    </div>
  );
};
